import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from 'src/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 select-none cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-primary text-foreground hover:bg-mint hover:text-white shadow-sm',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm',
        outline: 'border border-border text-foreground bg-white hover:bg-accent shadow-sm',
        secondary: 'bg-white text-foreground border border-secondary-foreground hover:bg-secondary shadow-sm',
        ghost: 'text-foreground hover:bg-accent',
        link: 'text-foreground underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8 py-2',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  ref?: React.RefObject<HTMLButtonElement | null>;
}

const Button = ({ className, variant, size, asChild = false, isLoading = false, children, ...props }: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      className={cn(
        buttonVariants({ variant, size, className: cn(className, isLoading && '[&_svg:nth-of-type(2)]:hidden') }),
      )}
      disabled={isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="size-4 animate-spin" />}
      <Slottable>{children}</Slottable>
    </Comp>
  );
};

export { Button, buttonVariants };
